import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import './QueuePage.css';
import { FaSpotify, FaSearch, FaPlus, FaCheck, FaLock, FaUnlock, FaClock, FaLightbulb, FaQrcode, FaCopy, FaCheckCircle, FaTimes, FaCamera, FaLink, FaCar } from 'react-icons/fa';
import {
    initiateLogin,
    handleCallback,
    isSessionUnlocked,
    getSessionExpiry,
    isAdminUser,
    getSession,
    getShareableUrl,
    extendSession,
    endSession,
    decodeSession,
} from './spotifyAuth';
import {
    getCurrentUser,
    searchTracks,
    addToQueue,
    getRandomSongs,
    getQueue,
} from './spotifyApi';
import {
    isTeslaAuthenticated,
    handleTeslaCallback,
    initiateTeslaLogin,
    registerTeslaDomain,
} from './teslaAuth';
import { getMyCarName, getArrivalInfo } from './teslaApi';

const QueuePage = ({ autoAuth = false }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [addedSongs, setAddedSongs] = useState([]);
    const [addingTrackId, setAddingTrackId] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [sessionExpiry, setSessionExpiry] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const [copied, setCopied] = useState(false);
    const searchTimeoutRef = useRef(null);

    // Song suggestions state
    const [suggestedSongs, setSuggestedSongs] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);

    // Now Playing / Queue state
    const [upcomingQueue, setUpcomingQueue] = useState([]);
    const [loadingQueue, setLoadingQueue] = useState(false);

    // QR Scanner / Manual entry state
    const [showScanner, setShowScanner] = useState(false);
    const [manualLink, setManualLink] = useState('');
    const [showManualInput, setShowManualInput] = useState(false);
    const scannerRef = useRef(null);

    // Tesla state
    const [carName, setCarName] = useState(null);
    const [teslaRegistering, setTeslaRegistering] = useState(false);
    const [teslaRegistered, setTeslaRegistered] = useState(false);
    const [arrivalInfo, setArrivalInfo] = useState(null);

    // Auto-auth flow stage: 'idle' | 'spotify' | 'tesla' | 'done'
    const [autoAuthStage, setAutoAuthStage] = useState(autoAuth ? 'spotify' : 'idle');

    // Handle Tesla domain registration
    const handleTeslaRegister = async () => {
        setTeslaRegistering(true);
        try {
            await registerTeslaDomain();
            setTeslaRegistered(true);
            setError(null);
            // Try to get car name again after registration
            const name = await getMyCarName();
            setCarName(name);
        } catch (err) {
            console.error('Tesla registration error:', err);
            setError('Tesla registration failed: ' + err.message);
        } finally {
            setTeslaRegistering(false);
        }
    };

    // Auto-auth flow: Start Spotify auth on mount if autoAuth mode
    useEffect(() => {
        if (autoAuthStage === 'spotify' && !isSessionUnlocked()) {
            // Start Spotify auth
            initiateLogin();
        } else if (autoAuthStage === 'spotify' && isSessionUnlocked()) {
            // Spotify already authenticated, move to Tesla
            setAutoAuthStage('tesla');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoAuthStage]);

    // Auto-auth flow: After Spotify success, start Tesla auth
    useEffect(() => {
        if (autoAuthStage === 'tesla') {
            if (!isTeslaAuthenticated()) {
                // Start Tesla auth
                initiateTeslaLogin();
            } else {
                // Tesla already authenticated, show QR
                setAutoAuthStage('done');
                setShowShareModal(true);
            }
        }
    }, [autoAuthStage]);

    // Auto-auth flow: After Tesla callback, show QR
    useEffect(() => {
        if (autoAuthStage === 'done' && isUnlocked && carName !== null) {
            setShowShareModal(true);
        }
    }, [autoAuthStage, isUnlocked, carName]);

    // Check initial session state
    useEffect(() => {
        const init = async () => {
            const code = searchParams.get('code');
            const state = searchParams.get('state');

            // Check if this is a Tesla OAuth callback (has state param)
            if (code && state) {
                try {
                    await handleTeslaCallback(code, state);
                    setSearchParams({});
                    window.history.replaceState(null, '', window.location.pathname + window.location.hash);
                    // Load car name after Tesla auth
                    const name = await getMyCarName();
                    setCarName(name);
                    // If in autoAuth flow, we're done - show QR
                    if (autoAuthStage === 'tesla' || autoAuth) {
                        setAutoAuthStage('done');
                        setShowShareModal(true);
                    }
                } catch (err) {
                    console.error('Tesla auth callback error:', err);
                    setError('Tesla authentication failed: ' + err.message);
                }
            } else if (code) {
                // Spotify OAuth callback (no state param)
                try {
                    // Handle OAuth callback
                    const session = await handleCallback(code);
                    setSearchParams({});

                    // Clear the code from URL and set hash with session
                    const url = getShareableUrl(session);
                    window.history.replaceState(null, '', url);

                    // Verify it's the admin
                    const user = await getCurrentUser();
                    const admin = isAdminUser(user);
                    setIsAdmin(admin);

                    if (!admin) {
                        setError('Only Divyesh can unlock the queue session.');
                        setIsLoading(false);
                        return;
                    }

                    setIsUnlocked(true);
                    setSessionExpiry(getSessionExpiry());
                    setShareUrl(url);

                    // If in autoAuth flow, move to Tesla stage
                    if (autoAuthStage === 'spotify' || autoAuth) {
                        setAutoAuthStage('tesla');
                    } else {
                        setShowShareModal(true); // Show share modal after auth
                    }
                } catch (err) {
                    console.error('Auth callback error:', err);
                    setError('Authentication failed. Please try again.');
                }
            } else {
                // Check if session exists (from URL hash or localStorage)
                const unlocked = isSessionUnlocked();
                setIsUnlocked(unlocked);
                if (unlocked) {
                    setSessionExpiry(getSessionExpiry());
                    const session = getSession();
                    if (session) {
                        setShareUrl(getShareableUrl(session));
                    }
                    // Check if current user is admin
                    try {
                        const user = await getCurrentUser();
                        setIsAdmin(isAdminUser(user));
                    } catch (err) {
                        // Not admin or failed to get user
                        setIsAdmin(false);
                    }
                }
            }

            // Load Tesla car name if authenticated
            if (isTeslaAuthenticated()) {
                try {
                    const name = await getMyCarName();
                    setCarName(name);
                } catch (err) {
                    console.error('Failed to load car name:', err);
                }
            }

            setIsLoading(false);
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, setSearchParams]);

    // Load song suggestions and queue when unlocked
    useEffect(() => {
        if (isUnlocked && suggestedSongs.length === 0 && !loadingSuggestions) {
            loadSongSuggestions();
        }
        if (isUnlocked) {
            loadQueue();
            // Refresh queue every 10 seconds
            const interval = setInterval(loadQueue, 10000);
            return () => clearInterval(interval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUnlocked]);

    // Tesla destination detection - auto end session when arriving
    useEffect(() => {
        // Only run for admin with Tesla connected
        if (!isAdmin || !carName || !isUnlocked) {
            setArrivalInfo(null);
            return;
        }

        const checkArrival = async () => {
            try {
                const arrivalInfo = await getArrivalInfo();
                setArrivalInfo(arrivalInfo);
                if (arrivalInfo && arrivalInfo.minutesToArrival <= 2) {
                    // Within 2 minutes of destination - end session
                    console.log(`Arriving at ${arrivalInfo.destination} in ${arrivalInfo.minutesToArrival} min - ending session`);
                    const newExpiry = endSession();
                    if (newExpiry) {
                        setSessionExpiry(newExpiry);
                    }
                }
            } catch (err) {
                // Silently fail - don't want to spam errors if Tesla API fails
                console.debug('Arrival check failed:', err);
                setArrivalInfo(null);
            }
        };

        // Check immediately and then every 30 seconds
        checkArrival();
        const interval = setInterval(checkArrival, 30000);
        return () => clearInterval(interval);
    }, [isAdmin, carName, isUnlocked]);

    const hasDestination = Boolean(arrivalInfo);
    const tripEndIndex = useMemo(() => {
        if (!arrivalInfo || upcomingQueue.length === 0) {
            return null;
        }

        const averageSongMinutes = 3.5;
        const estimatedSongs = Math.max(1, Math.ceil(arrivalInfo.minutesToArrival / averageSongMinutes));
        return Math.min(upcomingQueue.length - 1, estimatedSongs - 1);
    }, [arrivalInfo, upcomingQueue]);

    const loadQueue = async () => {
        try {
            setLoadingQueue(true);
            const data = await getQueue();
            setUpcomingQueue(data.queue.slice(0, 10)); // Show first 10
        } catch (err) {
            console.error('Failed to load queue:', err);
        } finally {
            setLoadingQueue(false);
        }
    };

    const loadSongSuggestions = async () => {
        setLoadingSuggestions(true);
        try {
            const songs = await getRandomSongs(4);
            setSuggestedSongs(songs);
        } catch (err) {
            console.error('Failed to load song suggestions:', err);
        } finally {
            setLoadingSuggestions(false);
        }
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleExtendSession = () => {
        const newExpiry = extendSession();
        if (newExpiry) {
            setSessionExpiry(newExpiry);
            // Update share URL with new session
            const session = getSession();
            if (session) {
                setShareUrl(getShareableUrl(session));
            }
        }
    };

    // Timer countdown
    useEffect(() => {
        if (!isUnlocked || !sessionExpiry) return;

        const updateTimer = () => {
            const remaining = sessionExpiry - Date.now();
            if (remaining <= 0) {
                setIsUnlocked(false);
                setTimeRemaining(null);
                setSessionExpiry(null);
                return;
            }

            const minutes = Math.floor(remaining / 60000);
            const seconds = Math.floor((remaining % 60000) / 1000);
            setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [isUnlocked, sessionExpiry]);

    // Debounced search
    const performSearch = useCallback(async (query) => {
        if (!query.trim()) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            const results = await searchTracks(query);
            setSearchResults(results);
        } catch (err) {
            console.error('Search error:', err);
            setError('Search failed. Please try again.');
        } finally {
            setIsSearching(false);
        }
    }, []);

    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchQuery.trim()) {
            searchTimeoutRef.current = setTimeout(() => {
                performSearch(searchQuery);
            }, 300);
        } else {
            setSearchResults([]);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, performSearch]);

    // Add song to queue
    const handleAddToQueue = async (track) => {
        setAddingTrackId(track.id);
        try {
            await addToQueue(track.uri);
            setAddedSongs((prev) => [
                {
                    id: track.id,
                    name: track.name,
                    artist: track.artists[0]?.name,
                    albumArt: track.album.images[2]?.url || track.album.images[0]?.url,
                    addedAt: Date.now(),
                },
                ...prev,
            ]);
        } catch (err) {
            console.error('Add to queue error:', err);
            setError(err.message || 'Failed to add song. Make sure Spotify is playing on a device.');
        } finally {
            setAddingTrackId(null);
        }
    };

    const isTrackAdded = (trackId) => addedSongs.some((song) => song.id === trackId);
    const formatArtists = (artists) => artists.map((a) => a.name).join(', ');

    const renderTrackItem = (track) => (
        <div key={track.id} className="track-item">
            <img
                src={track.album.images[2]?.url || track.album.images[0]?.url}
                alt={track.album.name}
                className="track-image"
            />
            <div className="track-info">
                <span className="track-name">{track.name}</span>
                <span className="track-artist">{formatArtists(track.artists)}</span>
            </div>
            <button
                className={`add-button ${isTrackAdded(track.id) ? 'added' : ''}`}
                onClick={() => handleAddToQueue(track)}
                disabled={addingTrackId === track.id || isTrackAdded(track.id)}
            >
                {addingTrackId === track.id ? (
                    <span className="adding">...</span>
                ) : isTrackAdded(track.id) ? (
                    <FaCheck />
                ) : (
                    <FaPlus />
                )}
            </button>
        </div>
    );

    if (isLoading) {
        return (
            <div className="queue-container">
                <div className="queue-loading">
                    <FaSpotify className="loading-spinner" />
                    <span>Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="queue-container">
            {/* Share Modal */}
            {showShareModal && (
                <div className="share-modal-overlay" onClick={() => setShowShareModal(false)}>
                    <div className="share-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Session Unlocked!</h2>
                        <p>Share this with your friends to let them add songs:</p>

                        <div className="qr-container">
                            <QRCodeSVG
                                value={shareUrl}
                                size={200}
                                bgColor="#121212"
                                fgColor="#1DB954"
                                level="M"
                            />
                        </div>

                        <p className="share-hint">Scan QR or share link</p>

                        <button className="copy-link-btn" onClick={handleCopyLink}>
                            {copied ? <FaCheckCircle /> : <FaCopy />}
                            {copied ? 'Copied!' : 'Copy Link'}
                        </button>

                        <button className="close-modal-btn" onClick={() => setShowShareModal(false)}>
                            Got it, let's go!
                        </button>
                    </div>
                </div>
            )}

            <div className="queue-header">
                <FaSpotify className="queue-icon" />
                <div className="queue-title-section">
                    <span className="queue-label">Divy's</span>
                    <h1 className="queue-title">Queue</h1>
                    <p className="queue-subtitle">
                        {isUnlocked
                            ? 'Add songs to my Spotify queue'
                            : 'Session locked... waiting for Divy to unlock'}
                    </p>
                </div>
                {isUnlocked && (
                    <div className="header-actions">
                        {/* Tesla car name display */}
                        {carName && (
                            <div className="car-badge">
                                <FaCar />
                                <span>{carName}</span>
                            </div>
                        )}
                        {/* Admin: Register Tesla if authenticated but no car name (412 error) */}
                        {isAdmin && isTeslaAuthenticated() && !carName && !teslaRegistered && (
                            <button
                                className="tesla-connect-btn"
                                onClick={handleTeslaRegister}
                                disabled={teslaRegistering}
                                title="Register Tesla"
                            >
                                <FaCar />
                            </button>
                        )}
                        {/* Admin: connect Tesla if not connected */}
                        {isAdmin && !isTeslaAuthenticated() && (
                            <button className="tesla-connect-btn" onClick={initiateTeslaLogin} title="Connect Tesla">
                                <FaCar />
                            </button>
                        )}
                        {timeRemaining && (
                            <div className="session-timer">
                                <FaClock />
                                <span>{timeRemaining}</span>
                                {isAdmin && (
                                    <button className="extend-timer-btn" onClick={handleExtendSession}>
                                        +
                                    </button>
                                )}
                            </div>
                        )}
                        <button className="share-btn" onClick={() => setShowShareModal(true)}>
                            <FaQrcode />
                        </button>
                    </div>
                )}
            </div>

            {error && (
                <div className="queue-error">
                    <span className="error-text">{error}</span>
                    <button className="error-dismiss" onClick={() => setError(null)}>
                        <FaTimes />
                    </button>
                </div>
            )}

            {!isUnlocked ? (
                <div className="queue-locked">
                    <div className="locked-content">
                        <FaLock className="locked-icon" />
                        <h2>Session Locked</h2>
                        <p>Ask Divy to share the QR code or link!</p>

                        {/* Join Options */}
                        <div className="join-options">
                            <button className="join-btn" onClick={() => setShowScanner(true)}>
                                <FaCamera /> Scan QR Code
                            </button>
                            <button className="join-btn secondary" onClick={() => setShowManualInput(true)}>
                                <FaLink /> Enter Link
                            </button>
                        </div>

                        {/* QR Scanner Modal */}
                        {showScanner && (
                            <div className="scanner-modal">
                                <div className="scanner-header">
                                    <span>Scan QR Code</span>
                                    <button onClick={() => {
                                        setShowScanner(false);
                                        if (scannerRef.current) {
                                            scannerRef.current.clear();
                                        }
                                    }}><FaTimes /></button>
                                </div>
                                <div id="qr-reader" ref={(el) => {
                                    if (el && !scannerRef.current) {
                                        const scanner = new Html5QrcodeScanner('qr-reader', {
                                            fps: 10,
                                            qrbox: { width: 250, height: 250 },
                                        });
                                        scanner.render((decodedText) => {
                                            // Extract hash from URL
                                            if (decodedText.includes('#s=')) {
                                                window.location.href = decodedText;
                                            }
                                        }, (error) => {
                                            // Ignore scan errors
                                        });
                                        scannerRef.current = scanner;
                                    }
                                }} />
                            </div>
                        )}

                        {/* Manual Link Input */}
                        {showManualInput && (
                            <div className="manual-input-modal">
                                <div className="manual-input-header">
                                    <span>Enter Share Link</span>
                                    <button onClick={() => setShowManualInput(false)}><FaTimes /></button>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Paste the share link here..."
                                    value={manualLink}
                                    onChange={(e) => setManualLink(e.target.value)}
                                    className="manual-link-input"
                                />
                                <button
                                    className="submit-link-btn"
                                    onClick={() => {
                                        if (manualLink.includes('#s=')) {
                                            window.location.href = manualLink;
                                        } else if (manualLink.startsWith('s=') || !manualLink.includes('#')) {
                                            // Try to decode directly as session token
                                            const token = manualLink.startsWith('s=') ? manualLink.slice(2) : manualLink;
                                            const session = decodeSession(token);
                                            if (session) {
                                                window.location.href = `/q#s=${token}`;
                                            } else {
                                                setError('Invalid link. Please try again.');
                                            }
                                        } else {
                                            setError('Invalid link. Please try again.');
                                        }
                                    }}
                                >
                                    Join Session
                                </button>
                            </div>
                        )}

                        <span
                            className="admin-login-trigger"
                            onClick={() => initiateLogin()}
                        >
                            ●
                        </span>
                    </div>
                </div>
            ) : (
                <div className="queue-content">
                    {/* Search Section */}
                    <div className="search-section">
                        <div className="search-bar">
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search for a song..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                            {searchQuery && (
                                <button className="clear-search-btn" onClick={() => setSearchQuery('')}>
                                    <FaTimes />
                                </button>
                            )}
                        </div>

                        {(searchResults.length > 0 || isSearching) && (
                            <div className="search-results">
                                {isSearching ? (
                                    <div className="search-loading">Searching...</div>
                                ) : (
                                    searchResults.map(renderTrackItem)
                                )}
                            </div>
                        )}
                    </div>

                    {/* Up Next Queue Section - moved above Ideas */}
                    {!searchQuery && upcomingQueue.length > 0 ? (
                        <div className="upcoming-section">
                            <h3 className="section-title">
                                <FaSpotify />
                                Up Next
                            </h3>
                            <div
                                className={`upcoming-list-wrapper ${hasDestination ? 'has-destination' : ''}`}
                                style={tripEndIndex !== null ? { '--trip-end-index': tripEndIndex } : undefined}
                            >
                                {hasDestination && (
                                    <div className="trip-progress" aria-hidden="true">
                                        {tripEndIndex !== null && <span className="trip-end-dot" />}
                                    </div>
                                )}
                                <div className="upcoming-list">
                                    {upcomingQueue.map((track, index) => (
                                        <div key={`${track.id}-${index}`} className="upcoming-item">
                                            <span className="upcoming-number">{index + 1}</span>
                                            <img
                                                src={track.album?.images[2]?.url || track.album?.images[0]?.url}
                                                alt=""
                                                className="upcoming-image"
                                            />
                                            <div className="upcoming-info">
                                                <span className="upcoming-name">{track.name}</span>
                                                <span className="upcoming-artist">
                                                    {track.artists?.map(a => a.name).join(', ')}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <button className="refresh-queue-btn" onClick={loadQueue} disabled={loadingQueue}>
                                {loadingQueue ? 'Refreshing...' : 'Refresh'}
                            </button>
                        </div>
                    )
                        // else, show info section
                        : (
                            <div className="info-section">
                                <p>
                                    <strong>Note:</strong> Make sure Spotify is playing on one of Divy's devices for the queue to work.
                                </p>
                            </div>
                        )
                    }

                    {/* Want Ideas? Section */}
                    {!searchQuery && suggestedSongs.length > 0 && (
                        <div className="ideas-section">
                            <h3 className="section-title">
                                <FaLightbulb />
                                Want ideas?
                            </h3>
                            <p className="ideas-subtitle">Random picks from Divy's music</p>
                            <div className="suggestions-list">
                                {suggestedSongs.map(renderTrackItem)}
                            </div>
                            <button className="shuffle-ideas-btn" onClick={loadSongSuggestions} disabled={loadingSuggestions}>
                                {loadingSuggestions ? 'Loading...' : 'Shuffle'}
                            </button>
                        </div>
                    )}

                    {/* Added Songs Section */}
                    {addedSongs.length > 0 && (
                        <div className="added-section">
                            <h3 className="section-title">
                                <FaUnlock />
                                Added to Queue
                            </h3>
                            <div className="added-list">
                                {addedSongs.map((song) => (
                                    <div key={`${song.id}-${song.addedAt}`} className="added-item">
                                        <img src={song.albumArt} alt="" className="added-image" />
                                        <div className="added-info">
                                            <span className="added-name">{song.name}</span>
                                            <span className="added-artist">{song.artist}</span>
                                        </div>
                                        <FaCheck className="added-check" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Admin: End Session button */}
            {isUnlocked && isAdmin && (
                <div className="end-session-section">
                    <button
                        className="end-session-btn"
                        onClick={() => {
                            const newExpiry = endSession();
                            if (newExpiry) {
                                setSessionExpiry(newExpiry);
                            }
                        }}
                    >
                        End Session
                    </button>
                </div>
            )}
        </div>
    );
};

export default QueuePage;
